import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import { RichTextDisplay } from '@factorialco/f0-react/dist/experimental'
import type { PurchaseRequestDetailQuery } from 'generated/resources/graphql'
import {
  MarkUploadDocumentTaskAsDoneDocument,
  PurchaseRequestDetailDocument,
  PurchaseRequestFragment,
} from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DropItem from 'modules/procurement/components/DropItem'

type ProcurementFile =
  | NonNullable<PurchaseRequestDetailQuery['procurement']['purchaseRequest']>['files'][number]
  | NonNullable<
      NonNullable<
        NonNullable<
          PurchaseRequestDetailQuery['procurement']['purchaseRequest']
        >['uploadDocumentTask']
      >[number]
    >['files'][number]

interface DownloadDocumentModalProps {
  isOpen: boolean
  onClose: () => void
  documentTask: PurchaseRequestFragment['uploadDocumentTask'][number] | null | undefined
}

const DownloadDocumentModal: React.FC<DownloadDocumentModalProps> = ({
  isOpen,
  onClose,
  documentTask,
}) => {
  const { employee } = useCurrentSession()
  const [downloadedFilesIds, setDownloadedFiles] = React.useState<number[]>([])

  const [markAsDone, { loading: isMarkingAsDone }] = useMutation(
    MarkUploadDocumentTaskAsDoneDocument,
    {
      extractorFn: (data) => data.procurementMutations.markAsDoneProcurementUploadDocumentTask,
      refetchQueries: [PurchaseRequestDetailDocument],
    }
  )

  const transformTaskableFileToProcurementFile = (file: {
    id: string | number
    filename: string
    url: string
    contentType?: string | null
    byteSize?: number | null
  }): ProcurementFile => {
    return {
      __typename: 'ProcurementFile',
      id: Number(file.id),
      filename: file.filename,
      url: file.url,
      contentType: file.contentType || '',
      byteSize: file.byteSize || 0,
    }
  }

  const handleFileDownload = React.useCallback(
    (fileId: number) => {
      const currentDownloadedFiles = downloadedFilesIds || []
      if (!currentDownloadedFiles.includes(fileId)) {
        setDownloadedFiles([...currentDownloadedFiles, fileId])
      }
    },
    [downloadedFilesIds]
  )

  const handleConfirm = useUnsafeCallback(async () => {
    if (!documentTask?.id || !employee?.id) return

    const hasFiles = !!documentTask.files?.length
    if (!hasFiles) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t(
          'tasks.types.procurement_request_document.upload_document.errors.file_not_attached'
        ),
      })
      return
    }

    const result = await markAsDone({
      variables: {
        uploadDocumentTaskId: documentTask.id,
        completedById: employee.id,
      },
    })

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t(
          'tasks.types.procurement_request_document.upload_document.errors.mark_as_done_failed'
        ),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t(
          'tasks.types.procurement_request_document.upload_document.success.mark_as_done'
        ),
      })
      onClose()
    }
  }, [documentTask, employee?.id, markAsDone, onClose])

  if (!documentTask) return null

  const hasFiles = documentTask.files && documentTask.files.length > 0
  const allFilesDownloaded = hasFiles
    ? documentTask.files.every((file) => downloadedFilesIds.includes(Number(file.id)))
    : false
  const disableConfirm = !hasFiles || !allFilesDownloaded || isMarkingAsDone

  return (
    <F0Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={i18n.t('procurement.purchase_requests.detail.download_document_modal.title')}
      primaryAction={{
        label: i18n.t('procurement.purchase_requests.detail.download_document_modal.confirm'),
        onClick: handleConfirm,
        loading: isMarkingAsDone,
        disabled: disableConfirm,
      }}
    >
      <Box flexDirection="column" gap="s12">
        {documentTask.taskContent && (
          <Box flexDirection="column" gap="s4">
            <RichTextDisplay
              content={documentTask.taskContent}
              className="text-f1-foreground-secondary"
            />
          </Box>
        )}

        {hasFiles && (
          <Box flexDirection="column" gap="s8">
            {documentTask.files.map((file) => {
              const procurementFile = transformTaskableFileToProcurementFile(file)
              return (
                <DropItem
                  key={file.id}
                  file={procurementFile}
                  onDownload={() => handleFileDownload(procurementFile.id)}
                />
              )
            })}
          </Box>
        )}
      </Box>
    </F0Dialog>
  )
}

export default DownloadDocumentModal
